/**
 * This component used get the image or video based on meta data received in props
 *
 * @module views/components/MediaWrapper
 * @memberof -Common
 */
import './MediaWrapper.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Video from '@ulta/core/components/Video/Video';
import { hasItems } from '@ulta/core/utils/array/array';

import Tag from '@ulta/modules/Tag/Tag';

import { addZoomInContainer } from '@ulta/utils/zoomUtils/zoomUtils';

/**
 * Represents a MediaWrapper component
 *
 * @method
 * @param {MediaWrapperProps} props - React properties passed from composition
 * @returns MediaWrapper
 */
export const MediaWrapper = function( props ){
  const { badges, componentKey, mediaContent, show } = props;
  const ref = useRef();

  if( mediaContent?.metaData?.mediaType === 'video' ){
    return (
      <div className={ classNames( 'MediaWrapper MediaWrapper__Video', { 'MediaWrapper--show': show } ) }>
        { hasItems( badges ) && badges.map( ( badge, index ) => (
          <Tag
            key={ `${componentKey}-MediaWrapper-Tag-${index}` }
            backgroundColor={ badge.backgroundColor }
            icon={ badge.icon }
            message={ badge.message }
            textColor={ badge.textColor }
            variant={ badge.variant }
          />
        ) ) }
        <Video
          imageUrl={ mediaContent.imageUrl }
          videoUrl={ mediaContent.videoUrl || '' }
          alt={ mediaContent.altText }
          closedCaptionUrl={ mediaContent.closedCaptionUrl }
          metaData={ mediaContent.metaData }
          ariaLabel={ mediaContent.altText }
          isCaptionsEnabled={ mediaContent.isCaptionsEnabled }
          videoProfiles={ mediaContent.videoProfiles }
        />
      </div>
    );
  }

  const src = mediaContent?.imageUrl?.split( '?' );
  const imgSrc = `${src?.[0]}?w=1080&h=1080&fmt=auto`;
  return (
    <div className={ classNames( 'MediaWrapper', { 'MediaWrapper--show': show } ) }>
      <div className='MediaWrapper__Image'
        onMouseMove={ addZoomInContainer }
        ref={ ref }
        style={ { backgroundImage: `url(${imgSrc})` } }
      >
        { hasItems( badges ) && badges.map( ( badge, index ) => (
          <Tag
            key={ `${componentKey}-MediaWrapper-Tag-${index}` }
            backgroundColor={ badge.backgroundColor }
            icon={ badge.icon }
            message={ badge.message }
            textColor={ badge.textColor }
            variant={ badge.variant }
          />
        ) ) }
        <img
          src={ imgSrc }
          width={ mediaContent.width }
          alt={ mediaContent.altText }
        />
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef MediaWrapperProps
 * @type {object}
 * @property {object} mediaContent - Sets the media data
 * @property {boolean} show - Show
 * @property {array} badges - shows badges
 */
export const propTypes =  {
  mediaContent: PropTypes.object.isRequired,
  show: PropTypes.bool,
  badges: PropTypes.array
};

MediaWrapper.propTypes = propTypes;

export default MediaWrapper;
